/**
 * @author      sammy.dowds@zumper.com (Sammy Dowds)
 * @copyright   Copyright (c) 2023, Zumper
 */

import { useMemo } from 'react'
import { ListingCardSizes } from './constants'

const CARD_HEIGHT_WITHOUT_CTAS = 293
const CARD_HEIGHT_WITH_CTAS = 353

export const useListingCardStyles = (
  size: ListingCardSizes,
  forceDesktop: boolean | undefined,
  forceMobile: boolean | undefined,
  hideCTAs: boolean | undefined,
  isChecked: boolean | undefined,
  showBorder: boolean
) => {
  return useMemo(() => {
    const isLargeCard = size === ListingCardSizes.LG
    const _cardBorder = isChecked
      ? ('2px solid var(--chakra-colors-pink-150)' as const)
      : ('1px solid var(--chakra-colors-gray-150)' as const)
    const cardBorder = isLargeCard
      ? ({
          base: forceDesktop ? _cardBorder : 'none',
          desktop: forceMobile ? 'none' : _cardBorder,
        } as const)
      : showBorder
        ? _cardBorder
        : 'none'

    const baseHeight = `${
      CARD_HEIGHT_WITHOUT_CTAS + (showBorder ? (isChecked ? 4 : 2) : 0)
    }px` as const
    const desktopHeight = `${
      CARD_HEIGHT_WITH_CTAS + (showBorder ? (isChecked ? 4 : 2) : 0)
    }px` as const

    const cardBorderRadius = isLargeCard
      ? ({
          base: forceDesktop
            ? 'var(--chakra-radii-lg)'
            : 'var(--chakra-radii-lg) var(--chakra-radii-lg) 0 0',
          desktop: 'var(--chakra-radii-lg)',
        } as const)
      : 'var(--chakra-radii-lg)'

    const cardHeight = isLargeCard
      ? ({
          base: forceDesktop ? '456px' : '436px',
          desktop: forceMobile ? '436px' : '456px',
        } as const)
      : typeof isChecked === 'boolean' || hideCTAs
        ? baseHeight
        : forceDesktop
          ? desktopHeight
          : forceMobile
            ? baseHeight
            : ({
                base: baseHeight,
                desktop: desktopHeight,
              } as const)

    const bodyHeight = isLargeCard
      ? ({
          base: forceDesktop ? '246px' : '228px',
          desktop: forceMobile ? '228px' : '246px',
        } as const)
      : typeof isChecked === 'boolean' || hideCTAs
        ? '141px'
        : ({
            base: forceDesktop ? '193px' : '142px',
            desktop: forceMobile ? '142px' : '193px',
          } as const)
    const bodyPadding = isLargeCard
      ? ({
          base: forceDesktop ? 'var(--chakra-space-4)' : '12px 0 0',
          desktop: forceMobile ? '12px 0 0' : 'var(--chakra-space-4)',
        } as const)
      : showBorder
        ? ({
            base: 'var(--chakra-space-3)',
            desktop: forceMobile ? 'var(--chakra-space-3)' : '10px 16px 0',
          } as const)
        : '8px 0 0'
    return {
      cardHeight,
      cardBorder,
      cardBorderRadius,
      bodyHeight,
      bodyPadding,
    } as const
  }, [size, forceDesktop, forceMobile, hideCTAs, isChecked, showBorder])
}
